import React, { createContext, useContext, useEffect, useRef } from 'react';

/**
 * RealtimeContext — single SSE connection to /api/realtime/stream, fanned
 * out to multiple subscribers via a topic pub/sub.
 *
 * Why not one EventSource per page? Browsers cap concurrent EventSource
 * connections to ~6 per origin, and each connection costs a backend
 * worker slot. One connection per tab + in-memory dispatch is the same
 * pattern we use for the inbox stream, just on a different SSE event
 * name (`event: realtime`).
 *
 * Topics published by the backend (see services/inbox_broker.publish_realtime):
 *   - calendar.invalidated         (any event mutation)
 *   - subscription.invalidated     (plan change / Stripe webhook)
 *   - event.created / event.updated / event.deleted / event.restored
 * Pages subscribe via the `useRealtime(topic, handler)` hook below.
 */
const RealtimeContext = createContext(null);

export const RealtimeProvider = ({ user, apiBaseUrl, children }) => {
  // Map<topic, Set<handler>>
  const subscribersRef = useRef(new Map());
  const esRef = useRef(null);

  useEffect(() => {
    // Don't open a stream until the user is authed. Reconnects on user change.
    if (!user) return;
    let cancelled = false;
    let retryDelay = 1000;
    let es = null;

    const connect = () => {
      if (cancelled) return;
      try {
        // EventSource sends cookies cross-origin only with the
        // {withCredentials:true} option AND a CORS-friendly server. Our
        // /api/realtime/stream sits behind the same ingress so cookies
        // flow naturally.
        es = new EventSource(`${apiBaseUrl}/realtime/stream`, { withCredentials: true });
        esRef.current = es;
        es.addEventListener('open', () => {
          retryDelay = 1000;
        });
        es.addEventListener('realtime', (evt) => {
          let data = null;
          try { data = JSON.parse(evt.data); } catch { return; }
          const topic = data?.topic;
          if (!topic) return;
          const handlers = subscribersRef.current.get(topic);
          if (handlers) {
            for (const fn of handlers) {
              try { fn(data); } catch (e) { /* never let one handler break others */ console.error('[realtime handler]', e); }
            }
          }
          // Fire wildcard listeners too (rare — useful for debugging panels).
          const wildcard = subscribersRef.current.get('*');
          if (wildcard) {
            for (const fn of wildcard) { try { fn(data); } catch {} }
          }
        });
        es.addEventListener('error', () => {
          // EventSource will auto-retry on its own, but if the server actively
          // closes (e.g. during a deploy) we manage our own backoff.
          if (es && es.readyState === 2 /* CLOSED */) {
            es.close();
            esRef.current = null;
            if (cancelled) return;
            setTimeout(connect, retryDelay);
            retryDelay = Math.min(retryDelay * 2, 30000);
          }
        });
      } catch (e) {
        console.warn('[realtime] EventSource failed', e);
      }
    };
    connect();

    return () => {
      cancelled = true;
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
    };
  }, [user, apiBaseUrl]);

  return (
    <RealtimeContext.Provider value={subscribersRef}>
      {children}
    </RealtimeContext.Provider>
  );
};

/**
 * Subscribe a component to one realtime topic. The handler runs whenever
 * the backend broadcasts an event with that topic for the user's org.
 * Use '*' to listen to every topic (debugging only).
 *
 * Topics support a trailing wildcard: `useRealtime('event.*', fn)`
 * subscribes to event.created, event.updated, etc.
 */
export const useRealtime = (topic, handler) => {
  const ctx = useContext(RealtimeContext);
  useEffect(() => {
    if (!ctx || !topic || typeof handler !== 'function') return;
    const map = ctx.current;

    // Wildcard support: register for each known sub-topic the backend emits
    // by tracking the prefix in a separate slot. Cheap because the dispatch
    // loop iterates the topic exact-match first; we add a meta-handler that
    // forwards prefix matches.
    const isPrefix = topic.endsWith('.*');
    const prefix = isPrefix ? topic.slice(0, -2) : null;

    let dispatcher = handler;
    if (isPrefix) {
      dispatcher = (data) => {
        if (typeof data?.topic === 'string' && data.topic.startsWith(prefix + '.')) {
          handler(data);
        }
      };
      // Listen on '*' for prefix matches.
      let set = map.get('*');
      if (!set) { set = new Set(); map.set('*', set); }
      set.add(dispatcher);
      return () => {
        set.delete(dispatcher);
        if (set.size === 0) map.delete('*');
      };
    }
    let set = map.get(topic);
    if (!set) { set = new Set(); map.set(topic, set); }
    set.add(dispatcher);
    return () => {
      set.delete(dispatcher);
      if (set.size === 0) map.delete(topic);
    };
  }, [ctx, topic, handler]);
};

export default RealtimeProvider;
